// Which business UI a PrivacyBee staff member wants: the new Hive portal, or
// the legacy app. It has to be a cookie on the shared Privacy Bee domain —
// the legacy app is a different origin and must read the same answer, which
// rules out the sessionStorage demo mode uses to stay inside one tab.
//
// The cookie is a VETO, never a GRANT. `is_pb` is the only thing that opens the
// new portal; this preference can only send you back to legacy. A customer who
// sets it by hand therefore gets exactly today's behaviour.
//
// Mirrored into a ref because `document.cookie` is not reactive: a computed
// reading it directly would cache the boot-time answer and never see the
// preference change mid-navigation.

import { ref } from 'vue';

export const BUSINESS_UI_COOKIE = 'pb_business_ui_preference';

export type BusinessUiPreference = 'new' | 'classic';

const SHARED_DOMAINS = ['.privacybee.test', '.privacybee.com'];

function readCookie(): string | null {
    if (import.meta.server) {
        return null;
    }

    const match = document.cookie.match(new RegExp('(^| )' + BUSINESS_UI_COOKIE + '=([^;]+)'));

    return match ? match[2] : null;
}

const preference = ref<string | null>(readCookie());

// Scoped to the shared parent domain when there is one, and host-only otherwise,
// so the cookie also works when Hive is served from a bare localhost port.
function cookieAttributes(): string {
    const parent = SHARED_DOMAINS.find((domain) => window.location.hostname.endsWith(domain));
    const secure = window.location.protocol === 'https:' ? ' secure;' : '';

    return `${parent ? ` domain=${parent};` : ''}${secure} samesite=lax`;
}

export function prefersClassicBusinessUi(): boolean {
    return preference.value === 'classic';
}

export function setBusinessUiPreference(value: BusinessUiPreference): void {
    // Hive is `ssr: false`, so this only ever runs in the browser — but
    // readCookie() above already guards the same way, and a cookie writer that
    // reaches for document/window unguarded is a trap for the day that changes.
    if (import.meta.server) {
        return;
    }

    const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
    document.cookie = `${BUSINESS_UI_COOKIE}=${value}; path=/; expires=${expires.toUTCString()};${cookieAttributes()}`;
    preference.value = value;
}
